import { connect } from "react-redux";
import * as resolver from "../factories/ComponentsFactory";
import getMapDispatchToProps from "../utils/getMapDispatchToProps";
import { elementBindToSelector as getMapStateToProps } from "../selectors/selectByBindTo";
import { selectImmutable } from "../selectors/selectByBindTo";
import utilityMethods from "../utils";
import { compareImmutableStates, getChildrenData } from "../utils/storeUtils";
import { resolveItemsBinding } from "../utils/bindingUtils";
import { createSelector } from "reselect";

const connectGlobalProperties = (propertyList = []) => {
    const selector = createSelector(
        [...propertyList.map((propertyName) => (state) => state.getIn(["global", propertyName]))],
        (...globalProperties) => {
            // console.log(counter++);
            let result = {};
            propertyList.forEach((name, index) => {
                result[propertyList[index]] = globalProperties[index];
            });
            return result;
        }
    );
    return connect(selector);
};

export default {
    resolve() {
        return resolver.resolve.apply(this, arguments);
    },
    getComponentResolver() {
        return resolver;
    },
    getMapStateToProps() {
        return getMapStateToProps;
    },
    getMapDispatchToProps() {
        return getMapDispatchToProps.apply(this, arguments);
    },
    getUtils() {
        return utilityMethods;
    },
    //connects to data and uiState
    connectComponent(inputProps = {}) {
        return connect(
            //mapStateToProps
            //uses standard connectComponent
            //includes uiState
            (state, props) => {
                let result = {};
                let ownData = selectImmutable(state, props);
                //add windowSize
                if (inputProps.windowSize) {
                    result.windowSize = state.getIn(["windowSize", "size"]);
                }
                //add globalProperties
                //inputProps.global is expected to be an Array
                if (inputProps.globalProperties && Array.isArray(inputProps.globalProperties)) {
                    let globalProperties = {};
                    inputProps.globalProperties.forEach(
                        (name) => (globalProperties[name] = state.getIn(["global", name]))
                    );
                    result.globalProperties = globalProperties;
                }
                //add component data
                result = Object.assign(result, ownData);
                //add uiState
                if (inputProps.uiState) {
                    result.uiState = state.getIn([
                        "uiState",
                        props.widgetId,
                        ownData.computedBindTo
                    ]);
                }
                //add uiStateTree for all paths that start with ownData.computedBindTo
                //for elements under the current component
                if (inputProps.uiStateTree) {
                    let widgetState = state.getIn(["uiState", props.widgetId]);
                    if (widgetState) {
                        result.uiStateTree = widgetState.filter((val, key) => {
                            return key.includes(ownData.computedBindTo);
                        });
                    }
                }
                //add childrenData
                if (inputProps.children) {
                    let parentProperties = {
                        widgetId: props.widgetId,
                        config: props.config,
                        data: ownData.data,
                        computedBindTo: ownData.computedBindTo
                    };
                    result.childrenData = getChildrenData(parentProperties, state);
                }
                return result;
            },
            //adding mapDispatchToProps only if needed
            inputProps.actions ? getMapDispatchToProps(inputProps.actions) : null,
            //mergeProps
            //transforming immutable data to mutable data
            (state, dispatchProps, ownProps) => {
                let mutableState = Object.assign({}, state);
                //checking for immutable data
                //mapStateToProps can return "data" as Object
                //Example: missing bindTo and child of a collection
                if (mutableState.data && mutableState.data.toJS) {
                    mutableState.data = mutableState.data.toJS();
                }
                if (mutableState.uiStateTree) {
                    mutableState.uiStateTree = state.uiStateTree.toJS();
                }

                if (mutableState.uiState) {
                    mutableState.uiState = state.uiState.toJS();
                } else {
                    //default uiState
                    mutableState.uiState = {};
                }

                if (mutableState.childrenData && mutableState.childrenData.length) {
                    mutableState.childrenData = mutableState.childrenData.map((item) => {
                        if (item.data) {
                            return Object.assign({}, item, { data: item.data.toJS() });
                        }
                        return item;
                    });
                }

                if (mutableState.globalProperties) {
                    Object.keys(mutableState.globalProperties).forEach((key) => {
                        if (
                            mutableState.globalProperties[key] &&
                            mutableState.globalProperties[key].toJS
                        ) {
                            mutableState.globalProperties[key] =
                                mutableState.globalProperties[key].toJS();
                        }
                    });
                }

                return Object.assign({}, mutableState, dispatchProps, ownProps);
            },
            {
                //doing smart comparison of immutable states
                areStatePropsEqual: (prevState, nextState) => {
                    let ownDataEqual = compareImmutableStates(prevState.data, nextState.data);
                    let uiStateEqual = true;
                    let childrenEqual = true;
                    let uiStateTreeEqual = true;
                    let windowSizeEqual = true;
                    let globalPropertiesEqual = true;
                    //check uiState
                    if (inputProps.uiState) {
                        uiStateEqual = compareImmutableStates(prevState.uiState, nextState.uiState);
                    }
                    //check uiState
                    if (inputProps.uiStateTree) {
                        uiStateTreeEqual = compareImmutableStates(
                            prevState.uiStateTree,
                            nextState.uiStateTree
                        );
                    }
                    //check window size
                    if (inputProps.windowSize) {
                        windowSizeEqual = prevState.windowSize === nextState.windowSize;
                    }
                    //check global properties
                    if (inputProps.globalProperties) {
                        if (prevState.globalProperties && nextState.globalProperties) {
                            Object.keys(prevState.globalProperties).forEach((key) => {
                                if (
                                    prevState.globalProperties[key] !==
                                    nextState.globalProperties[key]
                                ) {
                                    globalPropertiesEqual = false;
                                }
                            });
                        } else {
                            globalPropertiesEqual = false;
                        }
                    }
                    //check children
                    if (inputProps.children) {
                        childrenEqual =
                            prevState.childrenData.length === nextState.childrenData.length;

                        //comparing children immutable data separately
                        //will stop comparing after the first failed equality check
                        prevState.childrenData.forEach((item, index) => {
                            childrenEqual =
                                childrenEqual &&
                                compareImmutableStates(
                                    item.data,
                                    nextState.childrenData[index].data
                                );
                        });
                    }
                    return (
                        ownDataEqual &&
                        uiStateEqual &&
                        childrenEqual &&
                        uiStateTreeEqual &&
                        windowSizeEqual &&
                        globalPropertiesEqual
                    );
                }
            }
        );
    },

    connectComponentTable(dispatchProps) {
        return connect(
            //mapStateToProps
            //optimized for easier shallow comparison of immutable data
            //extracts data for the header and body
            //returns header and body objects as an array with elements in the format:
            // {
            //     data: immutableChildData,
            //     config: childConfig,
            //     computedBindTo: childComputedBindTo
            // }
            (state, props) => {
                let ownData = selectImmutable(state, props);
                let ownConfig = props.config || {};
                let bodyData = [];
                let headerData = [];
                let footerData = [];

                let tableEntities =
                    ownData.data && ownData.data.get && ownData.data.get("entities");

                let dataEntity = [];
                let descriptionEntity = [];
                let summaryEntity = [];

                if (tableEntities) {
                    dataEntity = tableEntities.find((entity) => {
                        return entity.get("class").includes("data");
                    });
                    descriptionEntity = tableEntities.find((entity) => {
                        return entity.get("class").includes("description");
                    });
                    summaryEntity = tableEntities.find((entity) => {
                        return entity.get("class").includes("summary");
                    });
                }

                if (ownConfig.body && dataEntity) {
                    let bodyProperties = {
                        widgetId: props.widgetId,
                        config: ownConfig.body,
                        data: dataEntity,
                        computedBindTo: ownData.computedBindTo + ".data"
                    };

                    bodyData = getChildrenData(bodyProperties, state);

                    bodyData.forEach((row, rowIndex) => {
                        //item binding
                        let rowProperties = {
                            widgetId: props.widgetId,
                            config: ownConfig.body.item,
                            data: row.data,
                            computedBindTo: row.computedBindTo
                        };
                        //items binding
                        if (!rowProperties.config) {
                            rowProperties.config = resolveItemsBinding(
                                ownConfig.body.items,
                                rowProperties.data.get("class").get(0)
                            );
                        }
                        bodyData[rowIndex].cellData = getChildrenData(rowProperties, state);
                    });
                }
                if (ownConfig.header && descriptionEntity) {
                    //resolve data for each child config
                    let headerProperties = {
                        widgetId: props.widgetId,
                        config: ownConfig.header,
                        data: descriptionEntity,
                        computedBindTo: ownData.computedBindTo + ".description"
                    };
                    headerData = getChildrenData(headerProperties, state);
                }

                if (ownConfig.footer && summaryEntity) {
                    //resolve data for each child config
                    let footerProperties = {
                        widgetId: props.widgetId,
                        config: ownConfig.footer,
                        data: ownData.data,
                        computedBindTo: ownData.computedBindTo + ".summary"
                    };
                    footerData = getChildrenData(footerProperties, state);
                }

                return Object.assign({}, ownData, {
                    bodyData,
                    headerData,
                    footerData
                });
            },
            //adding mapDispatchToProps only if needed
            dispatchProps ? getMapDispatchToProps(dispatchProps) : null,
            //mergeProps
            //transforming immutable data from selectImmutable to mutable data
            (state, dispatchProps, ownProps) => {
                let mutableState = Object.assign({}, state);
                //checking for immutable data
                //mapStateToProps can return "data" as Object
                //Example: missing bindTo and child of a collection
                if (mutableState.data && mutableState.data.toJS) {
                    mutableState.data = mutableState.data.toJS();
                }
                if (mutableState.headerData && mutableState.headerData.length) {
                    mutableState.headerData = mutableState.headerData.map((headerItem) => {
                        if (headerItem.data && headerItem.data.toJS) {
                            return Object.assign({}, headerItem, { data: headerItem.data.toJS() });
                        }
                        return headerItem;
                    });
                }
                if (mutableState.footerData && mutableState.footerData.length) {
                    mutableState.footerData = mutableState.footerData.map((footerItem) => {
                        if (footerItem.data && footerItem.data.toJS) {
                            return Object.assign({}, footerItem, { data: footerItem.data.toJS() });
                        }

                        return footerItem;
                    });
                }
                if (mutableState.bodyData && mutableState.bodyData.length) {
                    mutableState.bodyData = mutableState.bodyData.map((bodyItem) => {
                        let dataObject = {};
                        let cellDataObject = {};
                        if (bodyItem.data && bodyItem.data.toJS) {
                            dataObject.data = bodyItem.data.toJS();
                        }
                        if (bodyItem.cellData && bodyItem.cellData.length) {
                            cellDataObject.cellData = bodyItem.cellData.map((cell) => {
                                if (cell.data && cell.data.toJS) {
                                    return Object.assign({}, cell, { data: cell.data.toJS() });
                                }
                                return cell;
                            });
                        }
                        return Object.assign({}, bodyItem, dataObject, cellDataObject);
                    });
                }
                return Object.assign({}, mutableState, dispatchProps, ownProps);
            },
            {
                //doing smart comparison of immutable states including all children data
                areStatePropsEqual: (prevState, nextState) => {
                    let ownDataEqual = compareImmutableStates(prevState.data, nextState.data);

                    let headerDataEqual =
                        prevState.headerData.length === nextState.headerData.length;
                    let bodyDataEqual = prevState.bodyData.length === nextState.bodyData.length;
                    let footerDataEqual =
                        prevState.footerData.length === nextState.footerData.length;

                    //will stop comparing after the first failed equality check
                    prevState.headerData.forEach((item, index) => {
                        headerDataEqual =
                            headerDataEqual &&
                            compareImmutableStates(item.data, nextState.headerData[index].data);
                    });

                    prevState.bodyData.forEach((item, index) => {
                        bodyDataEqual =
                            bodyDataEqual &&
                            compareImmutableStates(item.data, nextState.bodyData[index].data);
                    });

                    prevState.footerData.forEach((item, index) => {
                        footerDataEqual =
                            footerDataEqual &&
                            compareImmutableStates(item.data, nextState.footerData[index].data);
                    });

                    return ownDataEqual && headerDataEqual && bodyDataEqual && footerDataEqual;
                }
            }
        );
    },
    connectWidget(dispatchProps) {
        return connect(
            //mapStateToProps
            //optimized selector for easier comparison of cached value
            (state, props) => {
                let widgetId = props.id;
                return {
                    config: state.getIn(["config", widgetId]),
                    data: state.getIn(["data", widgetId]),
                    uiState: state.getIn(["uiState", widgetId, widgetId]),
                    windowSizeCategory: state.getIn(["windowSize", "size"])
                };
            },
            getMapDispatchToProps(dispatchProps),
            //mergeProps
            (stateProps, dispatchProps, ownProps) => {
                let widget = ownProps.id;
                let config = stateProps.config ? stateProps.config.toJS() : {};
                let data = stateProps.data ? stateProps.data.toJS() : null;
                let uiState = stateProps.uiState ? stateProps.uiState.toJS() : null;
                //merging initial config with response config
                config = Object.assign({}, ownProps, config);

                return Object.assign(
                    {
                        id: widget,
                        widgetId: widget,
                        config,
                        data,
                        uiState,
                        windowSizeCategory: stateProps.windowSizeCategory
                    },
                    dispatchProps
                );
            },
            {
                //smart equality checks for immutable data
                areStatePropsEqual: (prev, next) => {
                    let sameConfig = compareImmutableStates(prev.config, next.config);
                    let sameData = compareImmutableStates(prev.data, next.data);
                    let sameUiState = prev.uiState === next.uiState;
                    let sameSize = prev.windowSizeCategory === next.windowSizeCategory;
                    //should reload on screen size change
                    let shoulReload = !sameSize && prev.config && prev.config.get("reloadOnResize");

                    return sameConfig && sameData && sameUiState && !shoulReload;
                }
            }
        );
    },
    connectFlow(dispatchProps) {
        return connect(
            //mapStateToProps
            //optimized selector for easier comparison of cached value
            (state, props) => {
                let widgetId = props.widgetId;
                return {
                    steps: state.getIn(["data", widgetId, "entities"]),
                    actions: state.getIn(["data", widgetId, "actions"]),
                    uiState: state.getIn(["uiState", widgetId, widgetId])
                };
            },
            getMapDispatchToProps(dispatchProps),
            //mergeProps
            (stateProps, dispatchProps, ownProps) => {
                let widget = ownProps.widgetId;
                let steps = stateProps.steps ? stateProps.steps.toJS() : [];
                let actions = stateProps.actions ? stateProps.actions.toJS() : [];
                let uiState = stateProps.uiState ? stateProps.uiState.toJS() : {};

                return Object.assign(
                    {
                        componentId: widget
                    },
                    {
                        steps,
                        actions,
                        uiState
                    },
                    ownProps,
                    dispatchProps
                );
            },
            {
                //smart equality checks for immutable data
                areStatePropsEqual: (prev, next) => {
                    let sameSteps = true;

                    let previousSteps = prev.steps;
                    let nextSteps = next.steps;

                    let existingSteps = previousSteps && nextSteps;
                    //check only the step properties on update
                    //to re-render the flow navigation header
                    if (existingSteps) {
                        if (previousSteps.length === nextSteps.length) {
                            //check step status changes
                            nextSteps.forEach((step, indx) => {
                                if (
                                    step.get("properties") !==
                                    previousSteps.get(indx).get("properties")
                                ) {
                                    sameSteps = false;
                                }
                            });
                        }
                    }

                    let sameActions = compareImmutableStates(prev.actions, next.actions);
                    let sameUiState = prev.uiState === next.uiState;
                    return sameSteps && sameActions && sameUiState;
                }
            }
        );
    },
    connectStep(dispatchProps) {
        return connect(
            //mapStateToProps
            //optimized selector for easier comparison of cached value
            (state, props) => {
                let widgetId = props.widgetId;
                let allStepsConfig = state.getIn([
                    "config",
                    widgetId,
                    "components",
                    0,
                    "components"
                ]);
                let stepConfig;
                if (allStepsConfig) {
                    stepConfig = allStepsConfig.find((item) => {
                        return props.class.includes(item.get("id"));
                    });
                }
                let stepData = state.getIn(["data", widgetId, "entities", props.index]);
                let stepId = stepData.get("step") || stepData.getIn(["class", 0]);

                let properties = state.getIn([
                    "data",
                    widgetId,
                    "entities",
                    props.index,
                    "properties"
                ]);
                return {
                    config: stepConfig,
                    properties: properties,
                    uiState: state.getIn(["uiState", widgetId, stepId]),
                    windowSizeCategory: state.getIn(["windowSize", "size"])
                };
            },
            getMapDispatchToProps(dispatchProps),
            //mergeProps
            (stateProps, dispatchProps, ownProps) => {
                let config = stateProps.config ? stateProps.config.toJS() : {};
                let status = stateProps.properties.status;
                let visible = stateProps.properties.visible;
                let windowSizeCategory = stateProps.windowSizeCategory;
                let uiState = stateProps.uiState ? stateProps.uiState.toJS() : {};

                return Object.assign(
                    {},
                    {
                        config,
                        status,
                        visible,
                        windowSizeCategory,
                        uiState
                    },
                    ownProps,
                    dispatchProps
                );
            },
            {
                //smart equality checks for immutable data
                areStatePropsEqual: (prev, next) => {
                    let sameConfig = prev.config === next.config;
                    let sameSize = prev.windowSizeCategory === next.windowSizeCategory;
                    //should reload on screen size change
                    let shoulReload = !sameSize && prev.config.get("reloadOnResize");
                    let sameProperties = prev.properties === next.properties;
                    let sameUiState = prev.uiState === next.uiState;

                    return sameConfig && !shoulReload && sameProperties && sameUiState;
                }
            }
        );
    },
    // Animation connect methods
    // for connecting animation components to webui state and tracking loading status
    // Usecase:
    // creating custom animation components and be more flexible when/where they are rendered.
    connectApplicationLoading() {
        return connectGlobalProperties(["applicationLoading"]);
    },
    connectGlobalLoading() {
        return connectGlobalProperties(["applicationLoading", "globalLoading"]);
    },
    connectWidgetLoading() {
        return connect((state, props) => {
            const widgetId = props.widgetId;
            return {
                applicationLoading: state.getIn(["global", "applicationLoading"]),
                globalLoading: state.getIn(["global", "globalLoading"]),
                widgetLoading: state.getIn(["uiState", widgetId, widgetId, "loading"])
            };
        });
    },
    connectGlobalProperties
};
